import { useMemo } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	Command,
	CommandInternal,
	CommandSections,
} from '../../../../common/types/commands/index.tsx';
import { getFlattenedResults } from '../../../../common/utils/get-flattened-results/index.tsx';
import { getGroupedCommands } from '../../../../common/utils/get-grouped-commands/index.tsx';
import { useCommandPalette } from '../../../command-palette/index.tsx';
import { filterCommandsFromSearch } from '../filter-commands-from-search/index.tsx';
import { createFuseFromCommands, searchFuseIndex } from '../search-commands/main.tsx';
import { useSearchableCommands } from '../search-commands/utils/use-searchable-commands/index.tsx';
import { sortCommands } from '../sort-commands/index.tsx';
import { applyAlwaysShowWithSearchResults } from '../apply-always-show-search-results/index.tsx';

export const useSortAndFilter = (
	search: string,
	selectedCommands: Command[],
	selectedSections?: CommandSections,
	filterCommandsOverride?: (
		search: string,
		selectedCommands: Command[],
		selectedSections?: CommandSections,
	) => Command[],
) => {
	const queryKeywordCount = useMemo(
		() => search.trim().split(' ').filter(Boolean).length,
		[search],
	);
	const searchableCommands = useSearchableCommands(selectedCommands);
	const [{ activeIndex }] = useCommandPalette();

	const fuse = useMemo(
		() => createFuseFromCommands(searchableCommands, queryKeywordCount),
		[searchableCommands, queryKeywordCount],
	);
	const sortedAndFilteredCommands = useMemo((): CommandInternal[] => {
		if (filterCommandsOverride) {
			return filterCommandsOverride(search, selectedCommands, selectedSections);
		}
		let filteredCommands;
		if (!search.trim().toLowerCase().length) {
			filteredCommands = selectedCommands.map((command) => ({
				...command,
				searchRelevancyScore: 1,
			}));
		} else {
			const fuseResults = searchFuseIndex(fuse, search);
			filteredCommands = filterCommandsFromSearch(search, fuseResults, searchableCommands);
		}
		return sortCommands(filteredCommands, selectedSections);
	}, [
		filterCommandsOverride,
		fuse,
		search,
		searchableCommands,
		selectedCommands,
		selectedSections,
	]);

	const groupedCommands = useMemo(() => {
		const grouped = getGroupedCommands(sortedAndFilteredCommands, selectedSections);

		if (fg('jjj_ask_rovo_compal_v1')) {
			// Apply commands marked with alwaysShowWithSearchResults when search has results
			if (search.trim() && sortedAndFilteredCommands.length > 0) {
				// Get IDs of commands already in the filtered results to avoid duplicates
				const existingCommandIds = new Set(sortedAndFilteredCommands.map((cmd) => cmd.id));

				// Only include alwaysShow commands that aren't already in the search results
				const alwaysShowCommands = selectedCommands.filter(
					(command) => command.alwaysShowWithSearchResults && !existingCommandIds.has(command.id),
				);

				if (alwaysShowCommands.length > 0) {
					return applyAlwaysShowWithSearchResults(grouped, alwaysShowCommands, selectedSections);
				}
			}
		}

		return grouped;
	}, [selectedSections, sortedAndFilteredCommands, search, selectedCommands]);

	const commandMatches = useMemo(
		() => getFlattenedResults(groupedCommands, activeIndex),
		[groupedCommands, activeIndex],
	);

	return commandMatches;
};
